import { useState, useEffect, useContext } from "react";
import { customFilterOptions, mainContext } from "@ts/constants";
import { IChannel, IMainContext, ITagTypeAggregate, TActiveClientId, TOverviewGrid } from "@ts/common-types";
import { useDispatch, useSelector } from "react-redux";
import ChangeInValueCellRenderer from "@/components/cell-renderers/ChangeInValueCellRenderer";
import { numericValueFormatter } from "@/ts/utils";
import { setTagTypeLevelTotalSpend } from "@/features/gridDataSlice";
import { filter, keys, map, reduce } from "underscore";
import AgGridLayout from "@/components/wrappers/layouts/AgGridLayout";
import { useGetModulesData } from "@/hooks/AllCustomHooks";

const TagTypeAggregateGrid: React.FC<TOverviewGrid> = ({ gridApi, setGridApi }) => {
    const dispatch = useDispatch();

    const funnelResponse = useSelector(
        (state: {
            funnelResponse: {
                value: Record<string, string | boolean | number>;
            };
        }) => state.funnelResponse.value
    );

    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );

    const channel = useSelector((state: IChannel) => state.channel.value);

    const { tagTypeAggregatedGridLoading } = useContext<IMainContext>(mainContext);

    const selectedTagTypeOption = useSelector(
        (state: { selectedTagTypeOption: { value: string } }) =>
            state.selectedTagTypeOption.value
    );

    const tagTypeAggregatedData = useSelector(
        (state: { tagTypeAggregatedData: { value: ITagTypeAggregate[] } }) =>
            state.tagTypeAggregatedData.value
    );

    const [rowData, setRowData] = useState<ITagTypeAggregate[]>(tagTypeAggregatedData);

    useEffect(() => {
        if (
            keys(funnelResponse).length &&
            funnelResponse.client_id === activeClientId
        ) {
            const filteredRowData = map(
                filter(
                    tagTypeAggregatedData,
                    (data) => data.tag_type === selectedTagTypeOption
                ),
                (filteredData) => {
                    const spendShare =
                        +(filteredData.spend / +funnelResponse?.spend) * 100;
                    const previousSpendShare =
                        +(
                            filteredData.previous_spend /
                            +funnelResponse?.previous_spend
                        ) * 100;
                    const saleShare =
                        +(filteredData.sale / +funnelResponse?.sale) * 100;
                    const previousSaleShare =
                        +(
                            filteredData.previous_sale /
                            +funnelResponse?.previous_sale
                        ) * 100;
                    return {
                        ...filteredData,
                        spend_share: spendShare,
                        previous_spend_share: previousSpendShare,
                        spend_share_change:
                            (spendShare / previousSpendShare - 1) * 100,
                        sale_share: saleShare,
                        previous_sale_share: previousSaleShare,
                        sale_share_change:
                            (saleShare / previousSaleShare - 1) * 100,
                    };
                }
            );

            dispatch(
                setTagTypeLevelTotalSpend(
                    reduce(filteredRowData, (sum, data) => sum + data.spend, 0)
                )
            );
            setRowData(filteredRowData);
        }
    }, [
        tagTypeAggregatedData,
        selectedTagTypeOption,
        activeClientId,
        channel,
        funnelResponse,
    ]);

    const columnDefs = useGetModulesData({
        type: "columns",
        overviewGridType: "tagTypeAggregate",
        colDefs: [
            {
                field: "tag",
                headerName: "Tag",
                filter: "agSetColumnFilter",
                sortable: true,
                minWidth: 180,
                maxWidth: 200,
            },
            {
                headerName: "Market Share",
                field: "market_share",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Spend % Share",
                field: "spend_share",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "Sales",
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sale % Share",
                field: "sale_share",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 2,
                },
            },
            {
                headerName: "Clicks",
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "RoAS",
                field: "roas",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Impressions",
                field: "impression",
                filter: "agNumberColumnFilter",
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                maxWidth: 200,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true
            },
            {
                field: "total_impression",
                headerName: "Total Impr.",
                minWidth: 140,
                maxWidth: 180,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                field: "impPercShare",
                headerName: "Impr. % Share",
                maxWidth: 180,
                minWidth: 140,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: false,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
        ],
    });

    return (
        <div
            className="ag-theme-alpine"
            id="tag-type-aggregated-grid"
            style={{ height: "20rem" }}
        >
            <AgGridLayout
                api={gridApi}
                rowData={rowData}
                onSelectionChanged={() => null}
                setGridApi={setGridApi}
                columnDefs={columnDefs}
                isTotalRowsReq={true}
                loading={tagTypeAggregatedGridLoading}
                rowHeight={65}
            ></AgGridLayout>
        </div>
    );
};

export default TagTypeAggregateGrid;